import * as React from 'react';
import * as _ from 'underscore';
import * as $ from 'jquery';
const compareObjects = require('fast-deep-equal');

import { IGlob } from '../../../../pdc-platform/src/ts/Glob';
import { IValuesCollection } from '../../ts/appTheme';
import { useElementsReorderingHook } from '../../ts/DragDropCommon';
import * as reactGlobalClick from '../../ts/reactGlobalClick';
import { EmptyPlaceholder, HiddenItemsButton, SelectedItemsPlaceholder } from './Buttons';
import { ProgressBar } from '../ProgressBar';
import { IUseDropdownSelectorStateHookObj, useDropdownSelectorStateHook, useElementsDivSizeHook } from './Hooks';
import { IDisplayValue, IPosition, ISize, IWidth } from '../../ts/Interfaces';
import { getCSSWidth } from '../../ts/Utilities';
import { ISuggestionsDropdownOptions, SuggestionsDropdown } from './SuggestionsDropdown';
import { areSuggestionValuesEqual, GetValuesResult, ISuggestionValue, IType } from '../SuggestionListField';

import {
    getSelectedItemStyle,
    getSelectedItemWidth,
    IGetElementComponent,
    IItemToShow,
    MIN_SELECTED_ITEM_WIDTH,
    normalizeTitle,
    SelectedItem
} from './SelectedItem';

interface ISelectedElementsDropdownOptions extends ISuggestionsDropdownOptions {
    allowNull: boolean;
    allowAdding: boolean;
    allowReordering: boolean;

    getDisplayValue(value: string): string;
    getElementComponent?: IGetElementComponent;

    onDropDownShow(e): void;
}

let _updateDropdownState: (values: ISuggestionValue[]) => void;

function dropdownStateUpdateHookFunction(callback: (values: ISuggestionValue[]) => void) {
    _updateDropdownState = (values: ISuggestionValue[]) => {
        callback(values);
    };
}

function allowReordering(allowReordering: boolean, readOnly: boolean, singleSelectMode: boolean) {
    return allowReordering && !readOnly && !singleSelectMode;
}

function processOnChange(newValue: ISuggestionValue[], onChange: (value: ISuggestionValue[]) => void) {
    if (onChange != null) {
        onChange(newValue != null ? [...newValue] : null);
    }
}

function SelectedElementsDropdown(props: {
    zIndex: number;
    options: ISelectedElementsDropdownOptions;

    close(): void;
}) {
    const handleOnClose = React.useCallback(() => {
        if (props.options.onClose != null) {
            props.options.onClose();
        }

        props.close();
    }, [props.options.onClose, props.close]);

    reactGlobalClick.useGlobalClickHook(handleOnClose, 1);

    const options = props.options;

    return (
        <div className="suggestion-select">
            <SelectedItemsDisplayComponent
                mainComponent={false}
                value={options.value}
                displayValues={options.displayValues}
                keySelect={options.keySelect}
                sortByDisplayValue={options.sortByDisplayValue}
                allowNull={options.allowNull}
                allowAdding={options.allowAdding}
                allowReordering={options.allowReordering}
                shortView={false}
                hideSearchField={options.hideSearchField}
                singleSelectMode={options.singleSelectMode}
                types={options.types}
                listThreshold={options.listThreshold}
                placeholder={options.placeholder}
                usePlaceholderAsTitle={options.usePlaceholderAsTitle}
                width={options.minWidth}
                readOnly={options.readOnly}
                theme={options.theme}
                name={options.name}
                setFocus={false}
                notSetFocusOnClose={options.notSetFocusOnClose}
                minSelectedItemsCellWidth={options.minSelectedItemsCellWidth}
                getValues={options.getValues}
                getDisplayValue={options.getDisplayValue}
                getElementComponent={options.getElementComponent}
                onDropDownShow={options.onDropDownShow}
                onChange={options.onChange}
            />
        </div>
    );
}

export interface ISelectedItemsDisplayComponentBaseProps {
    displayValues?: ISuggestionValue[];
    sortByDisplayValue?: boolean;
    keySelect?: boolean;
    types?: IType[];
    allowNull?: boolean; // Null value is allowed by default.
    allowAdding?: boolean;
    allowReordering?: boolean;
    singleSelectMode?: boolean;
    listThreshold?: number;
    placeholder?: string;
    usePlaceholderAsTitle?: boolean;
    tabIndex?: number;
    readOnly?: boolean;
    hideSelectAllAndClear?: boolean;
    hideSearchField?: boolean;
    width?: IWidth;
    minWidth?: IWidth;
    selectFirstItemByDefault?: boolean;
    checkValueExistsInValueList?: boolean;
    setFocus?: boolean; //onLoad
    notSetFocusOnClose?: boolean;
    minSelectedItemsCellWidth?: number;
    name?: string;
    theme?: IValuesCollection;
    glob?: IGlob;

    getValues?(): GetValuesResult;
    getDisplayValue?(value: string): string; // Is used to make custom formatting of displayed value.
    getElementComponent?: IGetElementComponent;
    onBlur?(value: ISuggestionValue[]): void;
    onFocus?(value: ISuggestionValue[]): void;
    onChange?(value: ISuggestionValue[]): void;
}

export interface ISelectedItemsDisplayComponentProps extends ISelectedItemsDisplayComponentBaseProps {
    mainComponent: boolean;
    shortView: boolean;
    value?: IDisplayValue[];

    onDropDownShow?(e: React.KeyboardEvent<HTMLDivElement> | React.MouseEvent<HTMLDivElement>): void;
}

export interface ISelectedItemsDisplayStatelessComponentProps extends ISelectedItemsDisplayComponentProps {
    stateObj: IUseDropdownSelectorStateHookObj;

    onCustomDropDownShow?(position: IPosition, size: ISize<number>, defaultSearchString: string): void;
}

export function SelectedItemsDisplayComponent(props: ISelectedItemsDisplayComponentProps) {
    const stateObj = useDropdownSelectorStateHook(props.value);

    return <SelectedItemsDisplayStatelessComponent stateObj={stateObj} {...props} />;
}

export function SelectedItemsDisplayStatelessComponent(props: ISelectedItemsDisplayStatelessComponentProps) {
    const [loading, setLoading] = React.useState(false);
    const { state, setValue, makeValueNotChanged } = props.stateObj;

    const selectedElementsRef = React.useRef<HTMLUListElement>(null);
    const componentRef = React.useRef<HTMLDivElement>(null);
    const elementsDivRef = React.useRef<HTMLDivElement>(null);

    const elementsDivSize = useElementsDivSizeHook(props.minSelectedItemsCellWidth, props.shortView, elementsDivRef);
    const fontWidth = React.useRef<number>(null);

    useElementsReorderingHook(
        componentRef,
        {
            axis: 'x',
            sortableItems: '.label-value',
            disable: !allowReordering(props.allowReordering, props.readOnly, props.singleSelectMode)
        },
        (start: number, stop: number) => {
            const newValue = [...state.value];
            const item = newValue.splice(start, 1);

            newValue.splice(stop, 0, item[0]);
            setValue(newValue, true);
            processOnChange(newValue, props.onChange);
        },
        [props.allowReordering, props.readOnly, props.singleSelectMode, props.onChange]
    );

    const setFocus = React.useCallback(() => {
        const container = componentRef.current;

        if (container != null) {
            setTimeout(() => {
                container.focus();
            }, 20);
        }
    }, []);

    React.useEffect(() => {
        const selectFirstItemByDefaultIsActive =
            props.selectFirstItemByDefault &&
            props.getValues != null &&
            props.onChange != null &&
            props.mainComponent &&
            (props.value == null || props.value.length === 0);
        const checkValueExistsInValueList =
            props.checkValueExistsInValueList &&
            props.getValues != null &&
            props.onChange != null &&
            props.mainComponent &&
            props.value != null &&
            props.value.length > 0;

        if (selectFirstItemByDefaultIsActive || checkValueExistsInValueList) {
            setLoading(true);

            props
                .getValues()
                .done((data) => {
                    if (data != null && data.displayValues != null) {
                        if (selectFirstItemByDefaultIsActive) {
                            const displayValues = data.displayValues;

                            if (displayValues == null || displayValues.length > 0) {
                                const selectedValue = [displayValues[0]];

                                setValue(selectedValue, true);
                                processOnChange(selectedValue, props.onChange);
                            }
                        } else if (checkValueExistsInValueList) {
                            const filteredValues = props.value?.filter((item) => {
                                return (
                                    data.displayValues.findIndex(
                                        (displayValue) => displayValue.value.toString() === item.value.toString()
                                    ) !== -1
                                );
                            });

                            setValue(filteredValues != null ? [...filteredValues] : [], true);
                        }
                    }
                })
                .always(() => {
                    setLoading(false);
                });
        } else {
            if (props.setFocus) {
                setFocus();
            }
        }
    }, []);

    React.useEffect(() => {
        if (!compareObjects(props.value, state.value)) {
            //console.log('SelectedItemsDisplayStatelessComponent: new value=' + JSON.stringify(props.value));
            setValue(props.value != null ? [...props.value] : [], true);
        }
    }, [props.value]);

    const handleOnDropDownChange = React.useCallback((newValue: ISuggestionValue[]) => {
        setValue(newValue != null ? [...newValue] : [], false);
    }, []);

    const handleOnDropDownClose = React.useCallback(() => {
        _updateDropdownState = null;

        if (state.dropDownIsVisible) {
            state.dropDownIsVisible = false;

            if (props.onBlur != null) {
                props.onBlur([...state.value]);
            }

            if (props.onChange != null) {
                const dataChanged = !compareObjects(state.oldValue, state.value);

                if (dataChanged) {
                    makeValueNotChanged();
                    processOnChange(state.value, props.onChange);
                }
            }

            if (!props.notSetFocusOnClose) {
                setFocus();
            }
        }
    }, [props.notSetFocusOnClose, props.onBlur, props.onChange]);

    const handleOnDropdownShow = React.useCallback(
        (e: React.KeyboardEvent<HTMLDivElement> | React.MouseEvent<HTMLDivElement>, defaultSearchString?: string) => {
            reactGlobalClick.trigger(e, 1);
            reactGlobalClick.cancel();

            if (state.dropDownIsVisible || props.readOnly) {
                return;
            }

            if (props.onFocus != null) {
                props.onFocus([...state.value]);
            }

            const component = $(componentRef.current);
            const offset = component.offset();
            const height = component.outerHeight();

            let minWidth = component.outerWidth();

            if (minWidth < 200) {
                minWidth = 200;
            }

            if (props.onCustomDropDownShow != null) {
                props.onCustomDropDownShow(
                    { top: offset.top, left: offset.left },
                    { height: height, width: minWidth },
                    defaultSearchString
                );
            } else {
                props.glob.DialogActions.createPopup<ISuggestionsDropdownOptions>('', {
                    top: offset.top,
                    left: offset.left,
                    minWidth: minWidth,
                    width: minWidth,
                    parentComponentHeight: height,

                    value: [...state.value],
                    displayValues: props.displayValues,
                    keySelect: props.keySelect,
                    sortByDisplayValue: props.sortByDisplayValue,
                    allowNull: props.allowNull,
                    allowAdding: props.allowAdding,
                    allowReordering: props.allowReordering,
                    singleSelectMode: props.singleSelectMode,
                    types: props.types,
                    listThreshold: props.listThreshold,
                    placeholder: props.placeholder,
                    usePlaceholderAsTitle: props.usePlaceholderAsTitle,
                    readOnly: props.readOnly,
                    hideSelectAllAndClear: props.hideSelectAllAndClear,
                    hideSearchField: props.hideSearchField,
                    defaultSearchString: defaultSearchString,
                    theme: props.theme,
                    name: props.name,
                    minSelectedItemsCellWidth: props.minSelectedItemsCellWidth,
                    updateStateHook: dropdownStateUpdateHookFunction,
                    getValues: props.getValues,
                    getDisplayValue: props.getDisplayValue,
                    onChange: handleOnDropDownChange,
                    onClose: handleOnDropDownClose,
                    component: SuggestionsDropdown
                });
            }

            state.dropDownIsVisible = true;

            if ((e as React.KeyboardEvent<HTMLDivElement>).code !== 'Tab') {
                e.stopPropagation();
                e.preventDefault();
            }
        },
        [
            state.dropDownIsVisible,
            state.value,
            props.readOnly,
            props.displayValues,
            props.keySelect,
            props.sortByDisplayValue,
            props.allowNull,
            props.allowAdding,
            props.allowReordering,
            props.singleSelectMode,
            props.types,
            props.listThreshold,
            props.placeholder,
            props.usePlaceholderAsTitle,
            props.readOnly,
            props.hideSelectAllAndClear,
            props.hideSearchField,
            props.theme,
            props.name,
            props.minSelectedItemsCellWidth,
            props.getValues,
            props.getDisplayValue,
            props.onFocus,
            props.onCustomDropDownShow
        ]
    );

    const handleOnElementClick = React.useCallback((e) => {
        e.stopPropagation();
    }, []);

    const handleOnValueElementRemove = React.useCallback(
        (newValue: ISuggestionValue) => {
            if (props.readOnly) {
                return;
            }

            const result = [...state.value];
            const valueIndex = result.findIndex((selectedValue) => {
                return areSuggestionValuesEqual(selectedValue, newValue);
            });

            result.splice(valueIndex, 1);
            setValue(result, true);
            processOnChange(result, props.onChange);

            if (props.mainComponent) {
                if (typeof _updateDropdownState === 'function') {
                    _updateDropdownState(result);
                }
            }
        },
        [state.value, props.readOnly, props.onChange]
    );

    const handleOnHiddenElementsDialogChange = React.useCallback(
        (newValue: ISuggestionValue[]) => {
            setValue(newValue, true);
            processOnChange(newValue, props.onChange);
        },
        [props.onChange]
    );

    const handleOnKeyDown = React.useCallback(
        (e: React.KeyboardEvent<HTMLDivElement>) => {
            if (e.code === 'Enter' || e.code === 'Space') {
                if (props.onDropDownShow != null) {
                    props.onDropDownShow(e);
                    return;
                }
            }

            const defaultSearchString = e.code?.indexOf('Key') === 0 ? e.key : undefined;

            if (defaultSearchString != null || e.code === 'ArrowDown') {
                handleOnDropdownShow(e, defaultSearchString);
            }
        },
        [props.onDropDownShow, handleOnDropdownShow]
    );

    const getOnValueElementClickHandler = () => {
        if (allowReordering(props.allowReordering, props.readOnly, props.singleSelectMode)) {
            return null;
        }

        return handleOnDropdownShow;
    };

    const handleOnClick = React.useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            if (props.onDropDownShow != null) {
                props.onDropDownShow(e);
            } else if (state.dropDownIsVisible) {
                handleOnDropDownClose();
            } else {
                handleOnDropdownShow(e);
            }
        },
        [props.onDropDownShow, handleOnDropDownClose, handleOnDropdownShow]
    );

    const { shortView, placeholder } = props;
    const numberOfItems = state.value.length;
    const placeholderIsDefined = placeholder != null && placeholder !== '';
    const elementsDivWidth = elementsDivSize.width;

    let content: React.JSX.Element;
    let numberOfItemsToShow = 0;

    if (loading) {
        content = <ProgressBar />;
    } else if (shortView && elementsDivWidth === 0) {
        content = <EmptyPlaceholder fontWidth={fontWidth} />;
    } else if (props.usePlaceholderAsTitle && placeholderIsDefined) {
        let title = placeholder;

        if (numberOfItems > 0) {
            title += ' (' + numberOfItems + ')';
        }

        content = (
            <SelectedItem
                title={title}
                limitedText={shortView}
                maxWidth={elementsDivWidth}
                minWidth={elementsDivWidth}
                getElementComponent={props.getElementComponent}
                onClick={getOnValueElementClickHandler()}
            />
        );
    } else {
        const itemsToShow: IItemToShow[] = [];

        let restWidth = elementsDivWidth;
        let showRemoveButton = props.allowNull !== false;

        if (!showRemoveButton && numberOfItems > 1) {
            showRemoveButton = true;
        }

        for (let i = 0; i < numberOfItems; i++) {
            const selectedValue = state.value[i];

            let title: string;

            if (props.keySelect) {
                title = selectedValue.displayValue;

                if (title == null) {
                    if (props.displayValues != null) {
                        for (const displayValue of props.displayValues) {
                            if (displayValue.value === selectedValue.value) {
                                title = displayValue.displayValue ?? displayValue.value;
                                break;
                            }
                        }
                    } else {
                        title = selectedValue.value;
                    }
                }
            } else {
                title = selectedValue.value;
            }

            title = normalizeTitle(title);

            if (props.getDisplayValue != null) {
                title = normalizeTitle(props.getDisplayValue(title));
            }

            let itemMaxWidth = elementsDivWidth;

            if (shortView) {
                const itemWidth = getSelectedItemWidth(fontWidth.current, title, showRemoveButton);

                if (i > 0) {
                    if (restWidth < itemWidth || restWidth < MIN_SELECTED_ITEM_WIDTH) {
                        break;
                    }

                    itemMaxWidth = restWidth;
                }

                restWidth -= itemWidth;
            }

            itemsToShow.push({
                displayValue: title,
                value: selectedValue.value,
                maxWidth: itemMaxWidth
            });
        }

        numberOfItemsToShow = itemsToShow.length;

        if (numberOfItemsToShow === 0) {
            content = <SelectedItemsPlaceholder text={placeholder} maxWidth={props.shortView && elementsDivWidth} />;
        } else {
            const itemStyle = getSelectedItemStyle(props.theme);
            const selectedElements = itemsToShow.map((item, index) => {
                return (
                    <li
                        key={'selectedItem_' + item.value + '_' + index}
                        className="pull-left label-value"
                        style={itemStyle}
                        onClick={handleOnElementClick}
                    >
                        <SelectedItem
                            value={item}
                            maxWidth={item.maxWidth}
                            limitedText={shortView}
                            showRemoveButton={showRemoveButton}
                            getElementComponent={props.getElementComponent}
                            onClick={getOnValueElementClickHandler()}
                            onRemove={handleOnValueElementRemove}
                        />
                    </li>
                );
            });

            content = (
                <ul className="list-unstyled item-selector-values-list" ref={selectedElementsRef}>
                    {selectedElements}
                </ul>
            );
        }
    }

    let numberOfHiddenItems: number;
    let handleOnHiddenItemsClick: React.MouseEventHandler<HTMLDivElement>;

    if (shortView && !props.singleSelectMode) {
        handleOnHiddenItemsClick = (e) => {
            reactGlobalClick.trigger(e, 1);
            reactGlobalClick.cancel();

            const component = $(componentRef.current);
            const offset = component.offset();
            const minWidth = component.outerWidth();

            props.glob.DialogActions.createPopup<ISelectedElementsDropdownOptions>('', {
                top: offset.top,
                left: offset.left,
                minWidth: minWidth,
                maxWidth: minWidth + 2,

                value: [...state.value],
                displayValues: props.displayValues,
                keySelect: props.keySelect,
                sortByDisplayValue: props.sortByDisplayValue,
                allowNull: props.allowNull,
                allowAdding: props.allowAdding,
                allowReordering: props.allowReordering,
                singleSelectMode: props.singleSelectMode,
                types: props.types,
                listThreshold: props.listThreshold,
                placeholder: props.placeholder,
                usePlaceholderAsTitle: props.usePlaceholderAsTitle,
                readOnly: props.readOnly,
                hideSelectAllAndClear: props.hideSelectAllAndClear,
                hideSearchField: props.hideSearchField,
                theme: props.theme,
                name: props.name != null ? props.name + '_HiddenElements' : undefined,
                minSelectedItemsCellWidth: props.minSelectedItemsCellWidth,
                updateStateHook: dropdownStateUpdateHookFunction,
                getValues: props.getValues,
                getDisplayValue: props.getDisplayValue,
                getElementComponent: props.getElementComponent,

                onChange: handleOnHiddenElementsDialogChange,
                onDropDownShow: handleOnDropdownShow,
                component: SelectedElementsDropdown
            });
        };

        numberOfHiddenItems = numberOfItems - numberOfItemsToShow;
    }

    const componentStyle: React.CSSProperties = {};
    const width = getCSSWidth(props.width);
    const minWidth = getCSSWidth(props.minWidth);

    if (width != null) {
        componentStyle.width = width;
    }

    if (minWidth != null) {
        componentStyle.minWidth = minWidth;
    }

    const height = shortView && elementsDivSize.height;
    const elementsDivStyle: React.CSSProperties = {
        width: '100%',
        overflowY: 'hidden'
    };

    if (height > 0) {
        elementsDivStyle.height = height + 'px';
    } else {
        elementsDivStyle.height = '100%';
    }

    return (
        <div
            className={'selected-items-component form-control' + (props.readOnly ? ' readonly' : '')}
            ref={componentRef}
            style={componentStyle}
            tabIndex={props.tabIndex ?? 0}
            onClick={handleOnClick}
            onKeyDown={handleOnKeyDown}
        >
            <table className="table-borderless w-100">
                <tbody>
                    <tr>
                        <td className="elements-cell">
                            <div ref={elementsDivRef} style={elementsDivStyle}>
                                {content}
                            </div>
                        </td>

                        <HiddenItemsButton
                            numberToShow={numberOfHiddenItems}
                            title={'Click to see all selected items'}
                            theme={props.theme}
                            onClick={handleOnHiddenItemsClick}
                        />

                        <td className="dropdown-toggle-cell">
                            <span className="glyphicon glyphicon-chevron-down"></span>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
}
